"use client";

import { type RefObject, useRef, useState } from "react";
import {
  ErrorNotice,
  Field,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildGraphSheet,
  dotsPath,
  type GridDrawing,
  type GridKind,
  type LengthUnit,
  linesPath,
  MAX_GRID_SPACING_MM,
  MM_PER_INCH,
  minGridSpacing,
  type Orientation,
  type PaperSize,
  type Sheet,
  type SheetResult,
  sheetFor,
  truePrintCss,
} from "@/lib/tool-kit/classroom/paper";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";

const SHEET_CLASS = "gp-sheet";
const PRESETS: Record<LengthUnit, string[]> = {
  in: ["1/8", "1/5", "1/4", "1/2", "1"],
  mm: ["1", "2", "5", "10"],
};
const DEFAULTS: Record<
  LengthUnit,
  { spacing: string; margin: string; major: string }
> = {
  in: { spacing: "1/4", margin: "1/2", major: "4" },
  mm: { spacing: "5", margin: "10", major: "0" },
};
const WEIGHTS = { fine: 0.1, medium: 0.2, bold: 0.35 } as const;
const MAJOR_CHOICES = ["0", "2", "4", "5", "10"] as const;

/** English strings. A translated content file overrides these through `ui`. */
export const GRAPH_PAPER_GENERATOR_STRINGS = {
  kindLabel: "Grid type",
  kinds: {
    square: "Square grid",
    dot: "Dot grid",
    isometric: "Isometric grid",
  },
  paperLabel: "Paper size",
  papers: { letter: "US Letter (8.5 × 11 in)", a4: "A4 (210 × 297 mm)" },
  orientationLabel: "Orientation",
  portrait: "Portrait",
  landscape: "Landscape",
  unitLabel: "Units",
  units: { in: "Inches", mm: "Millimeters" },
  unitShort: { in: "in", mm: "mm" },
  spacingLabel: "Spacing",
  spacingHint:
    "The side of a square, the distance between dots, or the side of a triangle. Fractions such as 1/4 work.",
  marginLabel: "Margin on each side",
  majorLabel: "Heavier line every",
  majorOff: "Off",
  weightLabel: "Line weight",
  weights: {
    fine: "Fine (0.1 mm)",
    medium: "Medium (0.2 mm)",
    bold: "Bold (0.35 mm)",
  },
  colorLabel: "Line color",
  squaresAcross: "one:{count} square across|other:{count} squares across",
  dotsAcross: "one:{count} dot across|other:{count} dots across",
  down: "{count} down",
  summaryLine: "{across}, {down}.",
  isometricSummary: "Equilateral triangles with {size} sides.",
  print: "Print",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG (300 dpi)",
  trueScale:
    "Prints at true size: in the print dialog choose the same paper, set Scale to 100% (Actual size), and turn off Fit to page and Headers and footers.",
  previewLabel: "{kind} on {paper}",
  badSpacing: "Enter a spacing between {min} and {max} {unit}.",
  badMargin: "That margin leaves no room for the grid. Enter a smaller margin.",
} as const;

type Weight = keyof typeof WEIGHTS;
type Strings = typeof GRAPH_PAPER_GENERATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

function inUnit(mm: number, unit: LengthUnit): number {
  return unit === "in" ? mm / MM_PER_INCH : mm;
}

function summaryFor(
  kind: GridKind,
  result: SheetResult<GridDrawing>,
  unit: LengthUnit,
  strings: Strings,
  locale: string,
  fmt: Format
): string {
  const { drawing } = result;
  if (!drawing) {
    return "";
  }
  if (kind === "isometric") {
    return t(strings.isometricSummary, {
      size: `${fmt(inUnit(result.spacing, unit), { maximumFractionDigits: 3 })} ${strings.unitShort[unit]}`,
    });
  }
  const extra = kind === "dot" ? 1 : 0;
  const across = drawing.columns + extra;
  const acrossText =
    kind === "dot"
      ? tPlural(strings.dotsAcross, locale, across, { count: fmt(across) })
      : tPlural(strings.squaresAcross, locale, across, { count: fmt(across) });
  return t(strings.summaryLine, {
    across: acrossText.trim(),
    down: t(strings.down, { count: fmt(drawing.rows + extra) }).trim(),
  });
}

function PaperSvg({
  drawing,
  kind,
  sheet,
  color,
  stroke,
  label,
  svgRef,
}: {
  drawing: GridDrawing;
  kind: GridKind;
  sheet: Sheet;
  color: string;
  stroke: number;
  label: string;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  const dots = kind === "dot";
  return (
    <svg
      aria-label={label}
      className={`${SHEET_CLASS} mx-auto block h-auto w-full max-w-xl rounded-sm border border-border bg-white shadow-sm`}
      height={sheet.cssHeight}
      preserveAspectRatio="xMidYMin slice"
      ref={svgRef}
      role="img"
      viewBox={`0 0 ${sheet.width} ${sheet.height}`}
      width={sheet.cssWidth}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect fill="#ffffff" height={sheet.height} width={sheet.width} />
      <path
        d={
          dots
            ? dotsPath(drawing.dots.filter((dot) => !dot.major))
            : linesPath(drawing.lines.filter((line) => !line.major))
        }
        fill="none"
        stroke={color}
        strokeLinecap={dots ? "round" : "butt"}
        strokeWidth={dots ? Math.max(0.5, stroke * 4) : stroke}
      />
      <path
        d={
          dots
            ? dotsPath(drawing.dots.filter((dot) => dot.major))
            : linesPath(drawing.lines.filter((line) => line.major))
        }
        fill="none"
        stroke={color}
        strokeLinecap={dots ? "round" : "square"}
        strokeWidth={dots ? Math.max(0.8, stroke * 6.5) : stroke * 2.5}
      />
    </svg>
  );
}

export default function GraphPaperGeneratorWidget() {
  const strings = useToolUi(GRAPH_PAPER_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const svgRef = useRef<SVGSVGElement>(null);
  const [kind, setKind] = useState<GridKind>("square");
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("portrait");
  const [unit, setUnit] = useState<LengthUnit>("in");
  const [spacingInput, setSpacingInput] = useState(DEFAULTS.in.spacing);
  const [marginInput, setMarginInput] = useState(DEFAULTS.in.margin);
  const [major, setMajor] = useState<(typeof MAJOR_CHOICES)[number]>("4");
  const [weight, setWeight] = useState<Weight>("fine");
  const [color, setColor] = useState("#7fa6d6");

  const sheet = sheetFor(paper, orientation);
  const result = buildGraphSheet(sheet, kind, unit, {
    spacing: spacingInput,
    margin: marginInput,
    majorEvery: Number(major),
  });
  const { drawing } = result;
  const fileName = `graph-paper-${kind}-${paper}-${spacingInput.replace("/", "-")}${unit}`;

  function changeUnit(next: LengthUnit) {
    setUnit(next);
    setSpacingInput(DEFAULTS[next].spacing);
    setMarginInput(DEFAULTS[next].margin);
    setMajor(DEFAULTS[next].major as (typeof MAJOR_CHOICES)[number]);
  }

  function save(format: "svg" | "png") {
    const svg = svgRef.current;
    if (!svg) {
      return;
    }
    if (format === "svg") {
      downloadSvg(`${fileName}.svg`, svg);
      return;
    }
    downloadPng(`${fileName}.png`, svg, { scale: 300 / MM_PER_INCH });
  }

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <style>{truePrintCss(`.${SHEET_CLASS}`, sheet)}</style>
      <div className="space-y-4 print:hidden">
        <PillGroup
          label={strings.kindLabel}
          name="gp-kind"
          onChange={setKind}
          options={(["square", "dot", "isometric"] as const).map((value) => ({
            value,
            label: strings.kinds[value],
          }))}
          value={kind}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="gp-paper"
            label={strings.paperLabel}
            onChange={setPaper}
            options={(["letter", "a4"] as const).map((value) => ({
              value,
              label: strings.papers[value],
            }))}
            value={paper}
          />
          <SelectField
            id="gp-orientation"
            label={strings.orientationLabel}
            onChange={setOrientation}
            options={[
              { value: "portrait", label: strings.portrait },
              { value: "landscape", label: strings.landscape },
            ]}
            value={orientation}
          />
          <SelectField
            id="gp-unit"
            label={strings.unitLabel}
            onChange={changeUnit}
            options={(["in", "mm"] as const).map((value) => ({
              value,
              label: strings.units[value],
            }))}
            value={unit}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <TextField
              hint={strings.spacingHint}
              id="gp-spacing"
              label={`${strings.spacingLabel} (${strings.unitShort[unit]})`}
              numeric
              onChange={setSpacingInput}
              value={spacingInput}
            />
            <div className="flex flex-wrap gap-2">
              {PRESETS[unit].map((preset) => (
                <button
                  aria-pressed={spacingInput === preset}
                  className={BUTTON_SMALL}
                  key={preset}
                  onClick={() => setSpacingInput(preset)}
                  type="button"
                >
                  {preset} {strings.unitShort[unit]}
                </button>
              ))}
            </div>
          </div>
          <TextField
            id="gp-margin"
            label={`${strings.marginLabel} (${strings.unitShort[unit]})`}
            numeric
            onChange={setMarginInput}
            value={marginInput}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="gp-major"
            label={strings.majorLabel}
            onChange={setMajor}
            options={MAJOR_CHOICES.map((value) => ({
              value,
              label: value === "0" ? strings.majorOff : value,
            }))}
            value={major}
          />
          <SelectField
            id="gp-weight"
            label={strings.weightLabel}
            onChange={setWeight}
            options={(["fine", "medium", "bold"] as const).map((value) => ({
              value,
              label: strings.weights[value],
            }))}
            value={weight}
          />
          <Field id="gp-color" label={strings.colorLabel}>
            <input
              className="h-10 w-full cursor-pointer rounded-lg border border-border bg-card p-1"
              id="gp-color"
              onChange={(event) => setColor(event.target.value)}
              type="color"
              value={color}
            />
          </Field>
        </div>
        {result.error === "spacing" ? (
          <ErrorNotice>
            {t(strings.badSpacing, {
              min: fmt(inUnit(minGridSpacing(kind), unit), {
                maximumFractionDigits: 3,
              }),
              max: fmt(inUnit(MAX_GRID_SPACING_MM, unit), {
                maximumFractionDigits: 3,
              }),
              unit: strings.unitShort[unit],
            })}
          </ErrorNotice>
        ) : null}
        {result.error === "margins" ? (
          <ErrorNotice>{strings.badMargin}</ErrorNotice>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            disabled={!drawing}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={!drawing}
            onClick={() => save("svg")}
            type="button"
          >
            {strings.downloadSvg}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={!drawing}
            onClick={() => save("png")}
            type="button"
          >
            {strings.downloadPng}
          </button>
        </div>
        <p aria-live="polite" className={HINT}>
          {summaryFor(kind, result, unit, strings, locale, fmt)}
        </p>
        <p className={HINT}>{strings.trueScale}</p>
      </div>
      {drawing ? (
        <PaperSvg
          color={color}
          drawing={drawing}
          kind={kind}
          label={t(strings.previewLabel, {
            kind: strings.kinds[kind],
            paper: strings.papers[paper],
          })}
          sheet={sheet}
          stroke={WEIGHTS[weight]}
          svgRef={svgRef}
        />
      ) : null}
    </div>
  );
}
